import { useCallback, useEffect, useRef, useState } from 'react';
import { AspectRatio } from './ui/aspect-ratio';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Plus } from 'lucide-react';
import { API_URL } from '../lib/api';

interface HeroPreview {
  heroId: string;
  previewUrl: string;
  directionName: string | null;
}

interface Site {
  id: string;
  name: string;
  heroPreviews?: HeroPreview[];
  selectedHeroId?: string;
}

interface TryAnotherDesignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site: Site;
  onSiteCreated: (siteId: string) => void;
}

function generateAltName(name: string): string {
  const match = name.match(/^(.+?)\s*\((\d+)\)$/);
  if (match) return `${match[1]} (${parseInt(match[2]) + 1})`;
  return `${name} (2)`;
}

export function TryAnotherDesignModal({
  open,
  onOpenChange,
  site,
  onSiteCreated,
}: TryAnotherDesignModalProps) {
  const [creatingHeroId, setCreatingHeroId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const gridNodeRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const gridRef = useCallback((node: HTMLDivElement | null) => {
    gridNodeRef.current = node;
  }, []);

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const node = gridNodeRef.current;
    if (!node || !open) return;

    const observer = new ResizeObserver(() => {
      setIsScrollable(node.scrollHeight > node.clientHeight);
    });
    observer.observe(node);

    // Check immediately on mount
    setIsScrollable(node.scrollHeight > node.clientHeight);

    return () => observer.disconnect();
  }, [open]);

  const heroes = site.heroPreviews || [];

  const handleBuild = async (heroId: string) => {
    // Abort any previous in-flight request
    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 30_000);

    setCreatingHeroId(heroId);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/v2/cloud/heroes/${heroId}/create-site`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: generateAltName(site.name) }),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error('Failed to create site');
      }

      const data = await response.json();
      setCreatingHeroId(null);
      onOpenChange(false);
      onSiteCreated(data.site.id);
    } catch (err) {
      // Don't update state if the component unmounted (abort on cleanup)
      if (controller.signal.aborted && !creatingHeroId) return;

      const isTimeout = err instanceof DOMException && err.name === 'AbortError';
      setError(isTimeout ? 'Request timed out. Please try again.' : (err instanceof Error ? err.message : 'Something went wrong'));
      setCreatingHeroId(null);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New site from another design</DialogTitle>
          <DialogDescription>
            Choose a different design for {site.name}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={gridRef}
          className="grid grid-cols-4 gap-6 mt-2 max-h-[70vh] overflow-y-auto pr-2 scrollbar-thin"
          style={isScrollable ? { maskImage: 'linear-gradient(to bottom, black calc(100% - 60px), transparent)', WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 60px), transparent)' } : undefined}
        >
          {heroes.map((hero) => {
            const isCreating = creatingHeroId === hero.heroId;
            const isDisabled = creatingHeroId !== null;

            return (
              <div key={hero.heroId} className="flex flex-col items-center gap-3">
                <div className="relative w-full rounded-lg overflow-hidden ring-1 ring-white/[0.06] bg-neutral-800/60">
                  <AspectRatio ratio={16 / 9}>
                    <div className="relative w-full h-full overflow-hidden">
                      <iframe
                        src={`${API_URL}${hero.previewUrl}`}
                        className="absolute inset-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none"
                        title={hero.directionName || 'Hero preview'}
                        loading="lazy"
                      />
                    </div>
                  </AspectRatio>
                </div>

                <Button
                  size="sm"
                  disabled={isDisabled}
                  onClick={() => handleBuild(hero.heroId)}
                >
                  {isCreating ? (
                    <span className="relative flex h-4 w-4 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current/40" />
                      <span className="relative inline-flex h-3 w-3 rounded-full bg-current/60" />
                    </span>
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {isCreating ? 'Creating...' : 'New Site'}
                </Button>
              </div>
            );
          })}
        </div>

        {error && (
          <p className="text-sm text-red-400 mt-2">{error}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
